import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { initialize } from 'redux-form';
import {
  Button,
} from '@unite-us/app-components';
import AuthorizationRequestForm from 'src/components/Referrals/ReferralFormFields/EditReferralDetails/AuthorizationRequestForm';
import DocumentUploader from 'common/form/DocumentUploader';
import _ from 'lodash';
import { CONTACT_CASE_FILE_UPLOAD_KEY, CASE_DETAILS_FORM } from 'src/components/Cases/constants';
import { fetchContactDocuments } from 'actions/Document/Contact/Group';
import { fetchGroupsPrograms } from 'actions/Program/Group';
import fetchProvidersUserCoreAction from 'src/actions/UserProvider/fetchProvidersUser';
import { fetchGroupsUsersFromProgram as fetchGroupsUsersFromProgramAction } from 'actions/User/Program/Group';
import { fetchCoreGroupForms as fetchCoreGroupFormsAction } from 'actions/Group';
import { fetchGroupContact } from 'src/actions/Contact/Group';
import {
  caseDetailFields,
} from 'src/components/Cases/utils';
import { paginateNetworkGroups, crtb2130MultipleClientNeeds, pays5650UpdateEditAuthorizations, pays5590RefactorInsuranceFilters } from 'src/common/utils/FeatureFlags/flags';
import CaseOONBrowse from './CaseOONBrowse';
import {
  CaseTypeAndNetworkSection,
  DescriptionSection,
  PersonConditionSection,
  ProgramSection,
  ServiceTypeAndWorkerSection,
} from './AddCaseDetailsFormSections';
import {
  buildLegacyFields,
  FormValuesSync,
  ProgramStateHandler,
  useAddCaseDetailsController,
} from './useAddCaseDetailsController';
import './AddCaseDetailsForm.scss';

const FormFooter = ({
  cancel,
  disableNextButton,
}) => (
  <footer className="add-case-details__footer">
    <Button
      id="add-case-details-cancel-btn"
      label="Cancel"
      onClick={cancel}
      style={{ marginRight: '10px' }}
    />

    <Button
      disabled={disableNextButton}
      id="add-case-details-next-btn"
      type="submit"
      label="Next"
      primary
    />
  </footer>
);

FormFooter.propTypes = {
  cancel: PropTypes.func.isRequired,
  disableNextButton: PropTypes.bool.isRequired,
};

export function AddCaseDetailsForm(props) {
  const {
    assistanceRequest,
    canPaginateNetworkGroups,
    caseDetailsValues,
    caseProgramEnrollment,
    clientNeedsFromQuery,
    contact,
    contactDocuments,
    contactId,
    currentUserGroup,
    debouncedSearchNetworkGroups,
    defaultProgram,
    fetchCoreGroupForms,
    fetchProvidersUserCore,
    fetchGroupsUsersFromProgram,
    fetchingOONGroups,
    group,
    groupForms,
    groupId,
    groupIsSensitive,
    groupsPrograms,
    groupsUsers,
    hasGroupLicense,
    hasNetworkLicense,
    initializeCaseDetailsForm,
    isCheckingProgramEnrollment,
    isCoordinationGroup,
    multipleClientNeedsFlag,
    networks,
    onServiceTypeChangeFetchEmpty,
    oonProgram,
    oonGroups,
    originCoordinates,
    paginateSearchNetworkGroups,
    programmingOptionsArray,
    programsServiceTypes,
    removeSelectedBrowseGroup,
    searchOONGroups,
    setBrowseMapGroups,
    shouldInitializeForm,
    styles,
    updateEditAuthorizationsEnabled,
    isRefactorInsuranceFiltersEnabled,
    user,
  } = props;

  const {
    cancel,
    debouncedFetchGroupsUsers,
    defaultProgramSelected,
    disableNextButton,
    feeScheduleProgram,
    feeScheduleProgramId,
    initialValues,
    initializeProgramState,
    invalidServiceType,
    onCaseTypeChange,
    onCreateCaseDetails,
    onNetworkChange,
    onProgramChange,
    onServiceTypeChange,
    personConditions,
    primaryWorkersReady,
    registerField,
    selectedProgramId,
    setDisableNextButton,
    showBrowse,
    showPersonCondition,
    triggerBrowse,
    validateClientNeedsField,
  } = useAddCaseDetailsController({
    assistanceRequest,
    canPaginateNetworkGroups,
    caseDetailsValues,
    clientNeedsFromQuery,
    contactDocuments,
    contactId,
    defaultProgram,
    fetchCoreGroupForms,
    fetchProvidersUserCore,
    fetchGroupsUsersFromProgram,
    group,
    groupForms,
    groupId,
    groupsPrograms,
    hasGroupLicense,
    hasNetworkLicense,
    multipleClientNeedsFlag,
    networks,
    oonProgram,
    paginateSearchNetworkGroups,
    searchOONGroups,
    shouldInitializeForm,
    user,
  });

  return (
    <div>
      <Form
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        onSubmit={onCreateCaseDetails}
        render={({ handleSubmit, form, values }) => {
          const legacyFields = buildLegacyFields(form, values);
          const isOONCase = values?.isOONCase || false;
          const showAuthorization = !_.isEmpty(feeScheduleProgram) &&
            !isOONCase && feeScheduleProgram?.attributes?.authorization_required;
          const disableOnEmptyCaseType = hasGroupLicense && _.isEmpty(values?.case_type);
          const oonGroupsFields = (legacyFields?.service_case?.oonGroups || []).map((org) => org.group.value);

          return (
            <>
              <FormValuesSync
                values={values}
                onChange={initializeCaseDetailsForm}
              />
              <ProgramStateHandler
                values={values}
                form={form}
                initializeProgramState={initializeProgramState}
                onProgramChange={onProgramChange}
                selectedProgramId={selectedProgramId}
                shouldInitializeForm={shouldInitializeForm}
              />

              <form className="add-case-details" onSubmit={handleSubmit}>
                <div className="add-case-details__reset mr-two">
                  <Button
                    id="clear-all-btn"
                    onClick={() => form.reset()}
                    style={{ minWidth: '25px' }}
                    label="Clear all"
                    icon={{
                      name: 'Delete',
                      position: 'right',
                    }}
                  />
                </div>

                <div className="add-case-details__content">
                  <CaseTypeAndNetworkSection
                    disableOnEmptyCaseType={disableOnEmptyCaseType}
                    form={form}
                    values={values}
                    group={group}
                    hasGroupLicense={hasGroupLicense}
                    hasNetworkLicense={hasNetworkLicense}
                    networks={networks}
                    onCaseTypeChange={onCaseTypeChange}
                    onNetworkChange={onNetworkChange}
                  />

                  <ProgramSection
                    disableOnEmptyCaseType={disableOnEmptyCaseType}
                    form={form}
                    values={values}
                    groupId={groupId}
                    hasGroupLicense={hasGroupLicense}
                    isCoordinationGroup={isCoordinationGroup}
                    programmingOptionsArray={programmingOptionsArray}
                    onProgramChange={onProgramChange}
                    user={user}
                    styles={styles}
                    caseProgramEnrollment={caseProgramEnrollment}
                    isCheckingProgramEnrollment={isCheckingProgramEnrollment}
                    registerField={registerField}
                  />

                  <ServiceTypeAndWorkerSection
                    canPaginateNetworkGroups={canPaginateNetworkGroups}
                    contact={contact}
                    debouncedFetchGroupsUsers={debouncedFetchGroupsUsers}
                    debouncedSearchNetworkGroups={debouncedSearchNetworkGroups}
                    defaultProgramSelected={defaultProgramSelected}
                    fetchingOONGroups={fetchingOONGroups}
                    form={form}
                    groupsUsers={groupsUsers}
                    invalidServiceType={invalidServiceType}
                    legacyFields={legacyFields}
                    onServiceTypeChange={onServiceTypeChange}
                    onServiceTypeChangeFetchEmpty={onServiceTypeChangeFetchEmpty}
                    oonGroups={oonGroups}
                    originCoordinates={originCoordinates}
                    primaryWorkersReady={primaryWorkersReady}
                    programsServiceTypes={programsServiceTypes}
                    registerField={registerField}
                    removeSelectedBrowseGroup={removeSelectedBrowseGroup}
                    toggleBrowse={triggerBrowse}
                    user={user}
                    values={values}
                  />

                  {multipleClientNeedsFlag && showPersonCondition && showAuthorization && (
                    <PersonConditionSection
                      personConditions={personConditions}
                      validateClientNeedsField={validateClientNeedsField}
                    />
                  )}

                  <AuthorizationRequestForm
                    contact={contact}
                    values={values}
                    form={form}
                    feeScheduleProgramId={feeScheduleProgramId || ''}
                    registerField={registerField}
                    setDisableNextButton={setDisableNextButton}
                    updateEditAuthorizationsEnabled={updateEditAuthorizationsEnabled}
                    isRefactorInsuranceFiltersEnabled={isRefactorInsuranceFiltersEnabled}
                  />

                  <DescriptionSection
                    defaultProgramSelected={defaultProgramSelected}
                    groupIsSensitive={groupIsSensitive}
                  />

                  {multipleClientNeedsFlag && showPersonCondition && !showAuthorization && (
                    <PersonConditionSection
                      personConditions={personConditions}
                      validateClientNeedsField={validateClientNeedsField}
                    />
                  )}

                  <div className="upload-case-documents">
                    <h5 className="upload-case-documents__label">Attach Documents</h5>
                    <DocumentUploader
                      dropzoneName={CONTACT_CASE_FILE_UPLOAD_KEY}
                      fields={legacyFields?.service_case || {}}
                      formOnly
                      registerField={registerField}
                    />
                  </div>
                </div>

                <FormFooter
                  cancel={cancel}
                  disableNextButton={disableNextButton}
                />
              </form>

              {showBrowse && (
                <CaseOONBrowse
                  contact={contact}
                  currentUserGroup={currentUserGroup}
                  description={values?.service_case?.description || ''}
                  fields={legacyFields}
                  oonGroups={canPaginateNetworkGroups ? oonGroups : oonGroupsFields}
                  sender={_.get(group, 'name', '')}
                  serviceType={values?.service_case?.service_type}
                  setBrowseMapGroups={setBrowseMapGroups}
                  toggleBrowse={triggerBrowse}
                />
              )}
            </>
          );
        }}
      />
    </div>
  );
}

AddCaseDetailsForm.propTypes = {
  assistanceRequest: PropTypes.object,
  caseDetailsValues: PropTypes.object,
  caseProgramEnrollment: PropTypes.object,
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  contact: PropTypes.object,
  contactDocuments: PropTypes.object.isRequired,
  contactId: PropTypes.string.isRequired,
  currentUserGroup: PropTypes.object,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  defaultProgram: PropTypes.object.isRequired,
  fetchCoreGroupForms: PropTypes.func.isRequired,
  fetchProvidersUserCore: PropTypes.func.isRequired,
  fetchGroupsUsersFromProgram: PropTypes.func.isRequired,
  fetchingOONGroups: PropTypes.bool.isRequired,
  group: PropTypes.object.isRequired,
  groupForms: PropTypes.array.isRequired,
  groupId: PropTypes.string.isRequired,
  groupIsSensitive: PropTypes.bool.isRequired,
  groupsPrograms: PropTypes.object.isRequired,
  groupsUsers: PropTypes.object.isRequired,
  hasGroupLicense: PropTypes.bool.isRequired,
  hasNetworkLicense: PropTypes.bool.isRequired,
  initializeCaseDetailsForm: PropTypes.func.isRequired,
  isCheckingProgramEnrollment: PropTypes.bool,
  isCoordinationGroup: PropTypes.bool.isRequired,
  networks: PropTypes.array,
  onServiceTypeChangeFetchEmpty: PropTypes.bool.isRequired,
  oonProgram: PropTypes.object.isRequired,
  oonGroups: PropTypes.array,
  originCoordinates: PropTypes.array.isRequired,
  paginateSearchNetworkGroups: PropTypes.func.isRequired,
  programmingOptionsArray: PropTypes.array.isRequired,
  programsServiceTypes: PropTypes.object,
  removeSelectedBrowseGroup: PropTypes.func.isRequired,
  searchOONGroups: PropTypes.func.isRequired,
  setBrowseMapGroups: PropTypes.func.isRequired,
  shouldInitializeForm: PropTypes.bool.isRequired,
  styles: PropTypes.object,
  user: PropTypes.shape({
    networks: PropTypes.array.isRequired,
  }).isRequired,
  multipleClientNeedsFlag: PropTypes.bool.isRequired,
  clientNeedsFromQuery: PropTypes.array,
  updateEditAuthorizationsEnabled: PropTypes.bool.isRequired,
  isRefactorInsuranceFiltersEnabled: PropTypes.bool.isRequired,
};

AddCaseDetailsForm.defaultProps = {
  assistanceRequest: {},
  caseDetailsValues: null,
  currentUserGroup: {},
  isCheckingProgramEnrollment: false,
  networks: [],
  styles: {
    iconTrash: {
      svg: {
        color: '#c5c8cc',
        fill: '#c5c8cc',
        width: '25px',
        height: '25px',
      },
    },
  },
  oonGroups: [],
  caseProgramEnrollment: {},
  programsServiceTypes: {},
  contact: {},
  clientNeedsFromQuery: [],
};

AddCaseDetailsForm.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  canPaginateNetworkGroups: paginateNetworkGroups(state),
  groupIsSensitive: _.get(state, 'session.currentProvider.attributes.sensitive', false),
  multipleClientNeedsFlag: crtb2130MultipleClientNeeds(state),
  updateEditAuthorizationsEnabled: pays5650UpdateEditAuthorizations(state),
  isRefactorInsuranceFiltersEnabled: pays5590RefactorInsuranceFilters(state),
  program: _.get(state, 'program'),
  caseDetailsValues: _.get(state, ['form', CASE_DETAILS_FORM, 'values'], null),
});

const mapDispatchToProps = (dispatch) => ({
  fetchContactDocuments: (...args) => dispatch(fetchContactDocuments(...args)),
  fetchGroupContact: (...args) => dispatch(fetchGroupContact(...args)),
  fetchCoreGroupForms: (...args) => dispatch(fetchCoreGroupFormsAction(...args)),
  fetchGroupsPrograms: (...args) => dispatch(fetchGroupsPrograms(...args)),
  fetchProvidersUserCore: (...args) => dispatch(fetchProvidersUserCoreAction(...args)),
  fetchGroupsUsersFromProgram: (...args) => dispatch(fetchGroupsUsersFromProgramAction(...args)),
  initializeCaseDetailsForm: (values) => dispatch(
    initialize(CASE_DETAILS_FORM, values, caseDetailFields),
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddCaseDetailsForm);
